import {
  ChatBubbleLeftEllipsisIcon,
  PencilIcon,
  PhoneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import {
  EventRow,
  EventText,
  EventUser,
  UserActionPattern,
} from '@/components/domain-events-v2/shared';
import { RegistryDefinitions } from '@/components/domain-events-v2/types';

export const noteEvents: RegistryDefinitions = {
  staff_note_created: {
    icon: ChatBubbleLeftEllipsisIcon,
    iconColor: 'text-gray-500',
    priority: 'medium',
    render: (event, inline) => {
      const { payload } = event;
      if (inline) {
        return (
          <EventRow>
            <EventUser event={event} />
            <EventText>commented</EventText>
          </EventRow>
        );
      }
      return (
        <MarkdownText
          content={payload.content}
          className="text-sm text-gray-700 mt-1"
          plainTextClassName="text-sm"
          markdownClassName="prose-sm text-gray-700 prose-p:my-1 prose-p:text-sm prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:text-sm prose-pre:text-xs prose-pre:bg-slate-100 prose-pre:px-2 prose-pre:py-1 prose-pre:border prose-pre:border-slate-200 prose-pre:rounded"
        />
      );
    },
    description: () => 'added a note',
  },

  staff_note_updated: {
    icon: PencilIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event, inline) => {
      const { payload } = event;
      if (inline) {
        return (
          <UserActionPattern event={event} action="updated their comment" />
        );
      }
      // Show the updated content
      return (
        <MarkdownText
          content={payload.content}
          className="text-sm text-gray-700 mt-1"
          plainTextClassName="text-sm"
          markdownClassName="prose-sm text-gray-700 prose-p:my-1 prose-p:text-sm prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:text-sm prose-pre:text-xs prose-pre:bg-slate-100 prose-pre:px-2 prose-pre:py-1 prose-pre:border prose-pre:border-slate-200 prose-pre:rounded"
        />
      );
    },
    description: () => 'edited a note',
  },

  staff_note_deleted: {
    icon: TrashIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => (
      <EventRow>
        <EventUser event={event} />
        <EventText>deleted their comment</EventText>
      </EventRow>
    ),
    description: () => 'removed a note',
  },
};

export const treatmentEvents: RegistryDefinitions = {
  treatment_check_in_note_logged: {
    icon: ChatBubbleLeftEllipsisIcon,
    iconColor: 'text-gray-500',
    priority: 'medium',
    render: (event, inline) => {
      const { payload } = event;
      if (inline) {
        return (
          <EventRow>
            <EventUser event={event} />
            <EventText>logged a treatment check-in note</EventText>
          </EventRow>
        );
      }
      return (
        <MarkdownText
          content={payload.general_notes}
          className="text-sm text-gray-700 mt-1"
          plainTextClassName="text-sm"
          markdownClassName="prose-sm text-gray-700 prose-p:my-1 prose-p:text-sm prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:text-sm prose-pre:text-xs prose-pre:bg-slate-100 prose-pre:px-2 prose-pre:py-1 prose-pre:border prose-pre:border-slate-200 prose-pre:rounded"
        />
      );
    },
    description: () => 'logged a treatment check-in note',
  },
};

export const voiceEvents: RegistryDefinitions = {
  voice_ai_call_completed: {
    icon: PhoneIcon,
    iconColor: 'text-purple-500',
    priority: 'medium',
    render: (event, inline) => {
      const { payload } = event;
      const provider = payload.provider_name || 'provider';
      if (inline) {
        return (
          <EventRow>
            <EventUser event={event} />
            <EventText>Voice AI called {provider}</EventText>
          </EventRow>
        );
      }
      return (
        <div className="text-sm text-gray-700 mt-1 space-y-1">
          {payload.request_status && (
            <div>
              <span className="font-semibold">Status:</span>{' '}
              {payload.request_status}
            </div>
          )}
          {payload.summary && (
            <div>
              <span className="font-semibold">Summary:</span> {payload.summary}
            </div>
          )}
        </div>
      );
    },
    description: () => 'completed a voice ai call',
  },
};
